/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type CIFSShare, type IpWhiteList } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { AddIpWhiteListComponent } from './add-ip-white-list/add-ip-white-list.component';
import { IpWhiteListService } from './ip-white-list.service';
import { IpWhiteListStrService } from './ip-white-list-str.service';
import { ModifyIpWhiteListComponent } from './modify-ip-white-list/modify-ip-white-list.component';
import { RemoveIpWhiteListComponent } from './remove-ip-white-list/remove-ip-white-list.component';

@Component({
    selector: 'app-ip-white-list',
    templateUrl: './ip-white-list.component.html',
    styleUrls: ['./ip-white-list.component.scss'],
})
export class IpWhiteListComponent extends TableAbstract<IpWhiteList> implements OnInit, OnChanges {
    @Input() cifsData: CIFSShare;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: IpWhiteList[] = [];
    loading = false;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;

    constructor(
        injector: Injector,
        private buttonService: ButtonService,
        private str: IpWhiteListStrService,
        private service: IpWhiteListService,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.checkShareChange(changes)) {
            this.selection = [];
            this.tableRest = this.service.getBatchRest(changes.cifsData.currentValue.vstoreId)
                .setFilters([
                    { field: 'cifsShareID', matchMode: '::', value: changes.cifsData.currentValue.ID },
                ]);
            this.tableCountRest = this.service.getCountRest(changes.cifsData.currentValue.ID, changes.cifsData.currentValue.vstoreId);
        }
    }

    /**
     * 判断CIFS共享是否切换
     */
    checkShareChange(data: SimpleChanges): boolean {
        const previous = data.cifsData.previousValue;
        const current = data.cifsData.currentValue;
        const isChange = (!previous && current) || (previous && current && current?.ID !== previous?.ID);

        return isChange;
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-ip-white-list',
            label: this.i18n.addBtn,
            theme: 'primary',
            permission: ['share_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-ip-white-list',
            label: this.i18n.modifyBtn,
            permission: ['share_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-ip-white-list',
            label: this.i18n.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: () => {
                this.remove(this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            menuItems: [
                this.modifyBtn,
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'rule',
                header: this.i18n.rules,
                filterType: 'search',
            },
            {
                field: 'ID',
                header: this.i18n.id,
                hidden: true,
                columnFilter: true,
            },
        ];
    }

    /**
     * 增加IP地址/地址段
     */
    add(): void {
        this.doAction(AddIpWhiteListComponent, {
            cifsData: this.cifsData,
        });
    }

    /**
     * 修改IP地址/地址段
     */
    modify(data: IpWhiteList): void {
        this.doAction(ModifyIpWhiteListComponent, {
            cifsData: this.cifsData,
            data,
        });
    }

    /**
     * 移除IP地址/地址段
     */
    remove(dataList: IpWhiteList[]): void {
        this.doAction(RemoveIpWhiteListComponent, {
            cifsData: this.cifsData,
            dataList,
        });
    }
}
